import React, { useState, useEffect, useMemo } from 'react';
import { usePortvilleRooms } from '../../../../hooks/admin/usePortvilleRooms';
import { BillGenerationRequest } from '../../../../types/portvilleBilling';

interface BillGenerationModalProps {
  onClose: () => void;
  onGenerate: (request: BillGenerationRequest) => Promise<void>;
  defaultPeriod?: { year: number; month: number };
}

const BillGenerationModal: React.FC<BillGenerationModalProps> = ({
  onClose,
  onGenerate,
  defaultPeriod
}) => {
  const { rooms, loading: roomsLoading } = usePortvilleRooms();
  
  const [selectedPeriod, setSelectedPeriod] = useState<{year: number, month: number}>(() => {
    if (defaultPeriod) {
      return defaultPeriod;
    }
    return {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1
    };
  });
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // 입주중인 호실만 필터링 (메모이제이션으로 불필요한 재계산 방지)
  const activeRooms = useMemo(() => {
    return rooms.filter(room => 
      room.status === 'occupied' && room.currentResident
    );
  }, [rooms]);

  // 전체 선택/해제 (selectAll 상태 변경 시에만 실행)
  useEffect(() => {
    if (selectAll) {
      setSelectedRooms(activeRooms.map(room => room.id));
    } else {
      setSelectedRooms([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAll]); // activeRooms 의존성 제거로 무한 루프 방지

  // 컴포넌트 마운트 시 초기 상태 설정
  useEffect(() => {
    setSelectedRooms([]);
    setSelectAll(false);
  }, []); // 빈 의존성 배열로 마운트 시에만 실행

  // 개별 호실 선택/해제
  const handleRoomSelect = (roomId: string) => {
    setSelectedRooms(prev => {
      const newSelection = prev.includes(roomId) 
        ? prev.filter(id => id !== roomId)
        : [...prev, roomId];
      
      // 전체 선택 상태 동기화
      if (newSelection.length === activeRooms.length && !selectAll) {
        setSelectAll(true);
      } else if (newSelection.length < activeRooms.length && selectAll) {
        setSelectAll(false);
      }
      
      return newSelection;
    });
  };

  // 청구서 생성 실행
  const handleGenerate = async () => {
    if (selectedRooms.length === 0) {
      alert('청구서를 생성할 호실을 선택해 주세요.');
      return;
    }

    setIsGenerating(true);
    try {
      // 현재 날짜를 기준으로 발행일 설정 (납부기한은 자동으로 청구월의 다음달 말일로 계산됨)
      const issuedDate = new Date();

      const request: BillGenerationRequest = {
        roomIds: selectedRooms,
        billingPeriod: selectedPeriod,
        issuedDate
      };

      await onGenerate(request);
      onClose();
    } catch (error) {
      console.error('청구서 생성 실패:', error);
      alert('청구서 생성에 실패했습니다.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* 모달 헤더 */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">청구서 생성</h2>
            <p className="text-gray-600 mt-1">선택한 호실들의 관리비 청구서를 생성합니다</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {/* 청구 기간 선택 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">청구 기간 선택</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">연도</label>
                <select
                  value={selectedPeriod.year}
                  onChange={(e) => setSelectedPeriod(prev => ({...prev, year: parseInt(e.target.value)}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  {Array.from({length: 3}, (_, i) => new Date().getFullYear() - 1 + i).map(year => (
                    <option key={year} value={year}>{year}년</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">월</label>
                <select
                  value={selectedPeriod.month}
                  onChange={(e) => setSelectedPeriod(prev => ({...prev, month: parseInt(e.target.value)}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                    <option key={month} value={month}>{month}월</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* 호실 선택 */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">호실 선택</h3>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  {selectedRooms.length}개 / {activeRooms.length}개 선택
                </span>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => setSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">전체 선택</span>
                </label>
              </div>
            </div>

            {/* 호실 목록 */}
            <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
              {roomsLoading ? (
                <div className="p-4 text-center text-gray-500">
                  호실 정보를 불러오는 중...
                </div>
              ) : activeRooms.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  입주중인 호실이 없습니다.
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {activeRooms.map((room) => (
                    <div key={room.id} className="p-4 hover:bg-gray-50">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedRooms.includes(room.id)}
                          onChange={() => handleRoomSelect(room.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium text-gray-900">
                                {room.roomNumber}
                              </div>
                              {room.currentResident && (
                                <div className="text-sm text-gray-600">
                                  {room.currentResident.name} · {room.currentResident.organization}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-gray-500">
                                {room.status === 'occupied' ? '입주중' : '준비중'}
                              </div>
                              <div className="text-sm text-gray-500">
                                계약기간: {room.occupancyPeriod?.startDate && 
                                  new Date(room.occupancyPeriod.startDate).toLocaleDateString()} ~
                              </div>
                            </div>
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 생성 옵션 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">생성 옵션</h3>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  defaultChecked={true}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  기존 청구서가 있는 경우 덮어쓰기
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  defaultChecked={true}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  관리비 설정이 없는 호실은 건너뛰기
                </span>
              </label>
            </div>
          </div>

          {/* 예상 결과 미리보기 */}
          {selectedRooms.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">생성 예상 결과</h4>
              <div className="text-sm text-blue-800">
                <div>• 생성 대상: {selectedRooms.length}개 호실</div>
                <div>• 청구 기간: {selectedPeriod.year}년 {selectedPeriod.month}월</div>
                <div>• 예상 소요 시간: 약 {Math.ceil(selectedRooms.length / 10)}초</div>
              </div>
            </div>
          )}
        </div>

        {/* 모달 푸터 */}
        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            disabled={isGenerating}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={handleGenerate}
            disabled={isGenerating || selectedRooms.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                생성 중...
              </div>
            ) : (
              '청구서 생성'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillGenerationModal;
